// Example integration in your Countries page
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCountries, deleteCountry } from "../../redux/slices/countriesSlice";
import CountriesTable from "../../components/Countries/CountriesTable";
import NewCountryModal from "../../components/Countries/NewCountryModal";
import EditCountryModal from "../../components/Countries/EditCountryModal";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useTranslation } from "../../contexts/LanguageContext";

function CountriesPage() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { countries, loading, error } = useSelector((state) => state.countries);

  // Top Bar state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState("Name");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [countryToEdit, setCountryToEdit] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // Filter and sort countries...
  const filteredCountries = countries.filter((country) => {
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      if (
        !country.countryName.toLowerCase().includes(lowerSearch) &&
        !country.countryCode.toLowerCase().includes(lowerSearch)
      ) {
        return false;
      }
    }
    if (filterStatus === "Active" && !country.isActive) return false;
    if (filterStatus === "Closed" && country.isActive) return false;
    return true;
  });
  const sortedCountries = [...filteredCountries].sort((a, b) => {
    if (sortBy === "Name") {
      return a.countryName.localeCompare(b.countryName);
    }
    if (sortBy === "Code") {
      return a.countryCode.localeCompare(b.countryCode);
    }
    return 0;
  });

  const openDeleteModal = (countryId) => {
    setCountryToDelete(countryId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!countryToDelete) return;
    
    setIsDeleting(true);
    dispatch(deleteCountry(countryToDelete))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: "Country deleted successfully!", type: "success" }));
        setIsDeleteModalOpen(false);
        setCountryToDelete(null);
      })
      .catch(() => {
        dispatch(showNotification({ message: "Failed to delete country", type: "error" }));
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleEdit = (country) => {
    setCountryToEdit(country);
    setIsEditModalOpen(true);
  };

  return (
    <div>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <CountriesTable
          countries={sortedCountries}
          onDeleteCountry={openDeleteModal}
          onEditCountry={handleEdit}
          onAddNew={() => setIsAddModalOpen(true)}
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          filterValue={filterStatus}
          onFilterChange={setFilterStatus}
          sortValue={sortBy}
          onSortChange={setSortBy}
        />
      )}
      {isAddModalOpen && (
        <NewCountryModal isOpen={isAddModalOpen} setIsOpen={setIsAddModalOpen} />
      )}
      {isEditModalOpen && countryToEdit && (
        <EditCountryModal
          isOpen={isEditModalOpen}
          setIsOpen={setIsEditModalOpen}
          country={countryToEdit}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t("deleteConfirm.country.title")}
        message={t("deleteConfirm.country.message")}
        confirmText={t("deleteConfirm.delete")}
        cancelText={t("deleteConfirm.cancel")}
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default CountriesPage;
